import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface Filial {
  id: number; codigo: string; razao_social: string; nome_fantasia: string;
  is_matriz: boolean; ativo: boolean; cidade?: string | null; uf?: string | null;
}

/** Componente reusavel: edita liberacao de produto OU pessoa por filial.
 * Vazio (nenhuma marcada) = liberado em todas as filiais (default).
 * Marcadas = liberado SOMENTE nas selecionadas.
 */
export default function FilialLiberacoes({
  entidade, entidadeId,
}: {
  entidade: 'produto' | 'pessoa'
  entidadeId: number
}) {
  const qc = useQueryClient()
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set())
  const [carregado, setCarregado] = useState(false)

  const url = entidade === 'produto'
    ? `/cadastros/produtos/${entidadeId}/filiais-liberadas`
    : `/cadastros/pessoas/${entidadeId}/filiais-liberadas`

  const { data: filiais = [], isLoading: loadingFiliais } = useQuery<Filial[]>({
    queryKey: ['filiais-todas-admin'],
    queryFn: async () => (await api.get('/filiais')).data,
  })

  const { data: liberacoes, isLoading: loadingLib } = useQuery<{ filiais_ids: number[] }>({
    queryKey: ['filial-liberacoes', entidade, entidadeId],
    queryFn: async () => (await api.get(url)).data,
  })

  useEffect(() => {
    if (liberacoes && !carregado) {
      setSelecionadas(new Set(liberacoes.filiais_ids))
      setCarregado(true)
    }
  }, [liberacoes, carregado])

  const salvar = useMutation({
    mutationFn: async () => (await api.put(url, { filiais_ids: [...selecionadas] })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['filial-liberacoes', entidade, entidadeId] })
      toast.success('Liberações salvas')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const toggle = (id: number) => {
    setSelecionadas(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id); else n.add(id)
      return n
    })
  }
  const liberarTodas = () => setSelecionadas(new Set())
  const restringirAtuais = () => setSelecionadas(new Set(filiais.filter(f => f.ativo).map(f => f.id)))

  const liberadoEmTodas = selecionadas.size === 0

  if (loadingFiliais || loadingLib) {
    return (
      <div className="text-center py-6 text-ink-400">
        <Loader2 size={16} className="animate-spin inline" /> Carregando...
      </div>
    )
  }

  if (filiais.length === 0) {
    return (
      <div className="card card-body bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div>
          Nenhuma filial cadastrada. Sem filiais, este {entidade} fica disponível normalmente.
          <a href="/configuracoes/filiais" className="ml-1 text-brand-700 underline">Cadastrar filial</a>.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className={`card card-body border-2 ${
        liberadoEmTodas ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-start gap-2">
          {liberadoEmTodas
            ? <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
            : <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />}
          <div className="flex-1">
            <div className="font-semibold text-sm">
              {liberadoEmTodas
                ? 'Liberado em TODAS as filiais'
                : `Restrito: liberado em ${selecionadas.size} de ${filiais.length} filial${filiais.length === 1 ? '' : 'is'}`}
            </div>
            <div className="text-xs text-ink-700 mt-0.5">
              {liberadoEmTodas
                ? 'Não há restrição. Marque uma ou mais filiais abaixo para restringir.'
                : 'Apenas as filiais marcadas conseguem usar este ' + entidade + ' em vendas/compras.'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={liberarTodas}
          className="btn-secondary text-xs">Liberar em todas</button>
        <button type="button" onClick={restringirAtuais}
          className="btn-secondary text-xs">Marcar todas</button>
      </div>

      <div className="card overflow-hidden">
        <div className="divide-y divide-ink-100">
          {filiais.map(f => {
            const marcada = selecionadas.has(f.id)
            return (
              <label key={f.id}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-ink-50 ${
                  marcada ? 'bg-brand-50/50' : ''
                } ${!f.ativo ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={marcada}
                  disabled={!f.ativo}
                  onChange={() => toggle(f.id)}
                  className="w-4 h-4" />
                <Building2 size={14} className="text-ink-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    <span className="truncate">{f.nome_fantasia}</span>
                    {f.is_matriz && (
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1 py-0.5 rounded shrink-0">Matriz</span>
                    )}
                    {!f.ativo && <span className="text-[9px] text-ink-400">inativa</span>}
                  </div>
                  <div className="text-xs text-ink-500 truncate">
                    {f.codigo}{f.cidade ? ` · ${f.cidade}/${f.uf}` : ''}
                  </div>
                </div>
              </label>
            )
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={() => salvar.mutate()} className="btn-primary"
          disabled={salvar.isPending}>
          {salvar.isPending
            ? <><Loader2 size={14} className="animate-spin" /> Salvando...</>
            : 'Salvar liberações'}
        </button>
      </div>
    </div>
  )
}
